import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, TrendingDown, AlertTriangle, Target, 
  BarChart3, Activity, Clock, Zap, DollarSign
} from 'lucide-react';
import { useEnhancedCredits } from '@/hooks/useEnhancedCredits';
import { Line, LineChart, Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export const CreditAnalyticsDashboard: React.FC = () => {
  const { 
    insights, 
    usageAnalytics, 
    serviceAnalytics,
    isLoadingAnalytics,
    isLoadingServices 
  } = useEnhancedCredits();

  if (isLoadingAnalytics || isLoadingServices) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="p-6">
            <div className="h-32 bg-muted animate-pulse rounded" />
          </Card>
        ))}
      </div>
    );
  }

  const getTrendIcon = (direction?: string) => {
    switch (direction) {
      case 'increasing':
        return <TrendingUp className="h-4 w-4 text-destructive" />;
      case 'decreasing':
        return <TrendingDown className="h-4 w-4 text-success" />;
      default:
        return <Activity className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getAnomalyColor = (score: number) => {
    if (score > 80) return 'text-destructive bg-destructive/10';
    if (score > 60) return 'text-warning bg-warning/10';
    if (score > 40) return 'text-primary bg-primary/10';
    return 'text-success bg-success/10';
  };

  return (
    <div className="space-y-6">
      {/* Usage Forecast */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Daily Usage</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {insights.forecast?.daily || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Credits per day
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Forecast</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {insights.forecast?.monthly || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Predicted usage
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Days Remaining</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {insights.forecast?.daysRemaining || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              At current usage rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Efficiency Score</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {insights.efficiency.score}%
            </div>
            <p className="text-xs text-muted-foreground">
              Usage optimization
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Usage Trend Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span>Usage Trend Analysis</span>
            {getTrendIcon(insights.trend)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Current Trend</span>
              <Badge variant="outline" className="capitalize">
                {insights.trend || 'Stable'}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Anomaly Detection</span>
              <Badge className={getAnomalyColor(insights.anomalyScore)}>
                {Math.round(insights.anomalyScore)}% Risk
              </Badge>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Optimization Potential</span>
                <span className="text-sm text-muted-foreground">
                  ${insights.optimizationPotential}/month
                </span>
              </div>
              <Progress 
                value={Math.min((insights.optimizationPotential / 100) * 100, 100)} 
                className="w-full"
              />
            </div>
          </div>

          {usageAnalytics && Array.isArray(usageAnalytics) && usageAnalytics.length > 0 && (
            <div className="mt-6">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={Array.isArray(usageAnalytics) ? usageAnalytics.slice().reverse() : []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="period_start" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString()}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => new Date(value).toLocaleDateString()}
                    formatter={(value: number) => [value, 'Credits']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="total_credits_consumed" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Service-Level Analytics */}
      <Card>
        <CardHeader>
          <CardTitle>Service Usage Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {serviceAnalytics && Array.isArray(serviceAnalytics) && serviceAnalytics.length > 0 ? (
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={Array.isArray(serviceAnalytics) ? serviceAnalytics : []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="service_type" />
                  <YAxis />
                  <Tooltip formatter={(value: number) => [value, 'Credits']} />
                  <Bar dataKey="credits_consumed" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array.isArray(serviceAnalytics) && serviceAnalytics.map((service) => (
                  <div key={service.id} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium capitalize">
                        {service.service_type.replace('_', ' ')}
                      </h4>
                      <Badge variant="outline">
                        {service.efficiency_score}% efficient
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>Usage: {service.usage_count} operations</p>
                      <p>Cost per op: {service.average_cost_per_operation} credits</p>
                      <p>Error rate: {service.error_rate}%</p>
                    </div>
                    {service.optimization_suggestions && Array.isArray(service.optimization_suggestions) && service.optimization_suggestions.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-warning">Suggestions:</p>
                        <ul className="text-xs text-muted-foreground">
                          {service.optimization_suggestions.map((suggestion, i) => (
                            <li key={i}>• {suggestion}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No service analytics data available yet</p>
              <p className="text-sm">Start using services to see breakdown</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Efficiency Recommendations */}
      {insights.efficiency.suggestions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Target className="h-5 w-5" />
              <span>Optimization Recommendations</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {insights.efficiency.suggestions.map((suggestion, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-primary/5 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{suggestion}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};